import { createFileRoute, Link } from "@tanstack/react-router";
import { CASE_STUDIES, BUSINESS, type CaseStudy } from "@/lib/site-data";

const BASE_URL = "https://lehitradessaeo.com";

export const Route = createFileRoute("/case-studies")({
  head: () => ({
    meta: [
      { title: `Case Studies | ${BUSINESS.shortName}` },
      {
        name: "description",
        content:
          "Detailed contractor SEO and Google Ads case studies — real numbers, real timelines, real client quotes.",
      },
      { property: "og:title", content: `Case Studies | ${BUSINESS.shortName}` },
      {
        property: "og:description",
        content: "How we doubled organic traffic, cut cost-per-lead, and won the Map Pack for trades contractors.",
      },
      { property: "og:url", content: `${BASE_URL}/case-studies` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${BASE_URL}/case-studies` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Case Studies",
          url: `${BASE_URL}/case-studies`,
          isPartOf: { "@id": `${BASE_URL}/#website` },
          mainEntity: {
            "@type": "ItemList",
            itemListElement: CASE_STUDIES.map((c, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: c.title,
              url: `${BASE_URL}/case-studies/${c.slug}`,
            })),
          },
        }),
      },
    ],
  }),
  component: CaseStudiesIndex,
});

function CaseStudiesIndex() {
  return (
    <main className="mx-auto max-w-5xl px-6 py-20">
      <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
        Case Studies
      </p>
      <h1 className="mt-3 font-display text-5xl tracking-tight md:text-6xl">
        Real numbers from real trades contractors
      </h1>
      <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
        Each case study below is a full breakdown — the challenge, the
        approach, the timeline, and the metrics. Names are anonymized at
        the client's request.
      </p>
      <div className="mt-12 divide-y divide-border border-y border-border">
        {CASE_STUDIES.map((c: CaseStudy) => (
          <article key={c.slug} className="py-8">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              {c.trade} · {c.city} · {c.timeline}
            </p>
            <h2 className="mt-2 font-display text-3xl tracking-tight">
              <Link
                to="/case-studies/$slug"
                params={{ slug: c.slug }}
                className="hover:text-primary"
              >
                {c.title}
              </Link>
            </h2>
            <p className="mt-3 text-muted-foreground">{c.summary}</p>
            <Link
              to="/case-studies/$slug"
              params={{ slug: c.slug }}
              className="mt-4 inline-block text-sm font-medium text-primary hover:underline"
            >
              Read the breakdown →
            </Link>
          </article>
        ))}
      </div>
      <p className="mt-8 text-xs text-muted-foreground">
        Example case studies based on representative engagements. Outcomes vary by market, budget, and starting position.
      </p>
    </main>
  );
}